import { forwardRef, useImperativeHandle } from "react";

// Component Imports
import { AGTable } from "@/components";

// Hooks Imports
import { useAGTable } from "./hooks/useAGTable";

// Types Imports
import type { BlotterAlgoTableProps, BlotterAlgoTableRef } from "./AlgoTable.types";

/**
 * AlgoTable - Presentational component for displaying blotter data in AG Grid.
 *
 * Refactored to be a pure presentational component:
 * - Receives data via props (no data fetching)
 * - Exposes expandAll() method via ref for parent control
 * - Error handling delegated to parent
 *
 * Performance:
 * - Uses useAGTable hook for memoized column definitions and detail renderer params
 * - AG Grid row virtualization enabled for performance with large datasets
 * - After pagination, scrolls to new rows to trigger rendering
 */
const AlgoTable = forwardRef<BlotterAlgoTableRef, BlotterAlgoTableProps>(
  ({ rowData, feedType, isLoading = false, sidebarPanelProps }, ref) => {
    // Hooks - START --------------------------------------------------------------
    const { gridRef, rowData: tableRowData, colDefs, defaultColDef, masterDetail, detailCellRendererParams, sideBar, onFirstDataRendered } =
      useAGTable({ feedType, rowData, sidebarPanelProps });
    // Hooks - END ----------------------------------------------------------------

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      toggleExpandAll: () => {
        if (!gridRef.current?.api) return;

        // Check if any rows are expanded
        let hasExpanded = false;
        gridRef.current.api.forEachNode((node) => {
          if ((node.group || node.master) && node.expanded) {
            hasExpanded = true;
          }
        });

        // Toggle all rows to opposite state
        gridRef.current.api.forEachNode((node) => {
          if (node.group || node.master) {
            node.setExpanded(!hasExpanded);
          }
        });
      },
      getRowCount: () => {
        return gridRef.current?.api?.getDisplayedRowCount() || 0;
      },
      ensureRowsVisible: (startIndex: number) => {
        if (gridRef.current?.api) {
          const modelUpdatedListener = () => {
            if (gridRef.current?.api) {
              const targetNode = gridRef.current.api.getDisplayedRowAtIndex(startIndex);
              if (targetNode) {
                gridRef.current.api.ensureNodeVisible(targetNode, "top");
              }
              gridRef.current.api.removeEventListener("modelUpdated", modelUpdatedListener);
            }
          };
          gridRef.current.api.addEventListener("modelUpdated", modelUpdatedListener);
        }
      },
      hasExpandedRows: () => {
        if (!gridRef.current?.api) return false;
        let hasExpanded = false;
        gridRef.current.api.forEachNode((node) => {
          if ((node.group || node.master) && node.expanded) {
            hasExpanded = true;
          }
        });
        return hasExpanded;
      },
    }), [gridRef]);

    return (
      <AGTable
        gridRef={gridRef}
        rowData={tableRowData}
        colDefs={colDefs}
        defaultColDef={defaultColDef}
        groupDisplayType="singleColumn"
        sideBar={sideBar}
        animateRows={false}
        loading={isLoading}
        tableHeight={750}
        masterDetail={masterDetail}
        detailCellRendererParams={detailCellRendererParams as React.ComponentProps<typeof AGTable>["detailCellRendererParams"]}
        suppressCellFocus
        suppressRowHoverHighlight
        debounceVerticalScrollbar
        onFirstDataRendered={onFirstDataRendered}
      />
    );
  }
);

AlgoTable.displayName = "AlgoTable";

export default AlgoTable;